/**
 * Real-Time Notification Toast Component
 * Displays animated notifications for WebSocket updates
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Bell, CheckCircle, TrendingUp, Award, X, Wifi, WifiOff } from 'lucide-react';

const NotificationToast = ({ notification, onClose, autoClose = 5000 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  
  const handleClose = useCallback(() => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose?.();
    }, 300);
  }, [onClose]);
  
  useEffect(() => {
    // Animate in
    const showTimer = setTimeout(() => setIsVisible(true), 10);
    
    // Auto close after delay
    let closeTimer;
    if (autoClose > 0) {
      closeTimer = setTimeout(() => {
        handleClose();
      }, autoClose);
    }
    
    return () => {
      clearTimeout(showTimer);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [autoClose, handleClose]);
  
  const getIcon = () => {
    switch (notification.type) {
      case 'evaluation_progress':
        return <TrendingUp className="w-5 h-5 text-blue-500" />;
      case 'evaluation_complete':
        return <Award className="w-5 h-5 text-green-500" />;
      case 'phase_update':
        return <CheckCircle className="w-5 h-5 text-purple-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };
  
  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'evaluation_progress':
        return 'bg-blue-50 border-blue-200';
      case 'evaluation_complete':
        return 'bg-green-50 border-green-200';
      case 'phase_update':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-slate-50 border-slate-200';
    }
  };
  
  // Memoize timestamp to avoid calling Date.now() during render
  const formattedTime = useMemo(() => {
    const timestamp = notification.timestamp || new Date().toISOString();
    return new Date(timestamp).toLocaleTimeString();
  }, [notification.timestamp]);
  
  return (
    <div
      className={`
        fixed top-4 right-4 z-50 max-w-sm w-full
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
    >
      <div className={`
        rounded-lg border shadow-lg p-4 
        ${getBackgroundColor()}
      `}>
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            {getIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900">
              {notification.type === 'evaluation_progress' && 'Evaluation Update'}
              {notification.type === 'evaluation_complete' && 'Evaluation Complete!'}
              {notification.type === 'phase_update' && 'Phase Update'}
              {notification.type === 'connected' && 'Connected'}
            </p>
            <p className="text-sm text-slate-600 mt-1">
              {notification.message}
            </p>
            {notification.progress !== undefined && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                  <span>Progress</span>
                  <span>{notification.progress}%</span>
                </div>
                <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                    style={{ width: `${notification.progress}%` }}
                  />
                </div>
              </div>
            )}
            {notification.certification_tier && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <Award className="w-4 h-4" />
                {notification.certification_tier} Certification
              </div>
            )}
            <p className="text-xs text-slate-400 mt-2">
              {formattedTime}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Connection Status Indicator Component
export const ConnectionStatus = ({ isConnected }) => {
  return (
    <div className={`
      inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium
      ${isConnected 
        ? 'bg-green-100 text-green-700' 
        : 'bg-red-100 text-red-700'
      }
    `}>
      {isConnected ? (
        <>
          <Wifi className="w-3 h-3" />
          Live
        </>
      ) : (
        <>
          <WifiOff className="w-3 h-3" />
          Offline
        </>
      )}
    </div>
  );
};

// Notification Manager Component
export const NotificationManager = ({ notifications, onDismiss }) => {
  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {notifications.map((notification, index) => (
        <NotificationToast
          key={notification.id || index}
          notification={notification}
          onClose={() => onDismiss(notification.id || index)}
          autoClose={notification.type === 'evaluation_complete' ? 10000 : 5000}
        />
      ))}
    </div>
  );
};

export default NotificationToast;
